import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Apple,
  Shirt,
  Cpu,
  Wrench,
  Sparkles,
  Hammer,
  ArrowRight,
} from "lucide-react";
import { useT } from "@/i18n/LanguageContext";

const PRODUCTS_BG =
  "https://images.pexels.com/photos/35531300/pexels-photo-35531300.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CATEGORY_META = [
  { icon: Apple, n: "01", img: "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=900&q=80" },
  { icon: Shirt, n: "02", img: "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=900&q=80" },
  { icon: Cpu, n: "03", img: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=900&q=80" },
  { icon: Wrench, n: "04", img: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=900&q=80" },
  { icon: Sparkles, n: "05", img: "https://images.unsplash.com/photo-1556228720-195a672e8a03?auto=format&fit=crop&w=900&q=80" },
  { icon: Hammer, n: "06", img: "https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?auto=format&fit=crop&w=900&q=80" },
];

export default function Products() {
  const { t } = useT();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-20%", "20%"]);

  return (
    <section
      id="products"
      ref={ref}
      data-testid="products-section"
      className="relative bg-slate-950 py-28 lg:py-40 overflow-hidden"
    >
      {/* Parallax background */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 will-change-transform">
        <img
          src={PRODUCTS_BG}
          alt=""
          aria-hidden="true"
          className="w-full h-[140%] object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-950/85 to-slate-950" />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="text-[#FF6B00] text-xs tracking-[0.24em] uppercase font-bold">
                {t.products.overline}
              </span>
              <div className="h-px w-16 bg-white/30" />
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.025em] leading-[1.02] text-white">
              {t.products.title1}<br />
              <span className="text-white/55">
                {t.products.title2}
              </span>
            </h2>
          </motion.div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {t.products.items.map((item, i) => {
            const meta = CATEGORY_META[i];
            const Icon = meta.icon;
            const c = { ...meta, ...item };
            return (
            <motion.article
              key={c.name}
              data-testid={`product-category-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: i * 0.07 }}
              className="group relative overflow-hidden rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/10 hover:border-white/20 transition-all duration-300"
            >
              <div className="aspect-[4/3] overflow-hidden relative">
                <img
                  src={c.img}
                  alt={c.name}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent" />
                <div className="absolute top-4 left-4 flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-white/15 backdrop-blur-md border border-white/20 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-white" strokeWidth={2.3} />
                  </div>
                  <span className="font-display text-xs font-bold text-white/70 tracking-[0.18em]">
                    {c.n}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-display text-xl font-semibold text-white mb-2 tracking-tight">
                  {c.name}
                </h3>
                <p className="text-sm text-white/65 leading-relaxed mb-5">
                  {c.desc}
                </p>
                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#FF6B00] hover:gap-3 transition-all"
                >
                  {t.products.itemCta}
                  <ArrowRight className="w-4 h-4" strokeWidth={2.4} />
                </a>
              </div>
            </motion.article>
          );
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm"
        >
          <div>
            <h3 className="font-display text-2xl font-semibold text-white mb-1">
              {t.products.banner}
            </h3>
            <p className="text-white/60 text-sm">
              {t.products.bannerSub}
            </p>
          </div>
          <a
            href="#contact"
            data-testid="products-discuss-cta"
            className="inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-slate-100 px-6 h-12 rounded-full font-semibold text-sm transition-colors"
          >
            {t.products.bannerCta}
            <ArrowRight className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
